import {
  RightOutlined,
  PlusSquareOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Cascader, Divider, Space, Spin, Tag } from 'antd';
import { has, isEmpty } from 'lodash-es';
import React, { Fragment, ReactNode, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MultiApp, useAppConfig } from '@/config/context';
import { getParamFromUrl } from '@/infrastructure/history';
import { getAppIcon, getPlatformName, Platform } from '@/infrastructure/utils/app';
import { allowCreateProduct } from '@/presentation/components/allow-create-product';
import { AppSlot } from '@/presentation/components/app-slot';
import { CreateApp } from '@/presentation/components/pro-app-list/create-app';
import { useStores } from '@/presentation/store';
import { IProductInfo } from '@/service/pro-user';
import { IProductType, ProductType } from '@/service/product-permissions';
import style from './index.module.scss';

interface SelectedOptionsType {
  key?: string | null;
  value?: string | null;
  label: ReactNode;
  pid?: number | null;
  appName?: string | null;
}

export const AppList = () => {
  const history = useHistory();
  const [keywords, setKeywords] = useState('');
  const LIMIT_FREE_PRODUCT = 4;
  const config = useAppConfig();
  const appRef = useRef<HTMLDivElement>(null);
  const [popupVisible, setPopVisible] = useState(false);
  const { productId, product, products, loadProducts, changeProduct, loadDemoProducts,
    productType } = useStores().ProductStore;
  const [loadStatus, setLoadStatus] = useState<undefined | 'loading' | 'loaded'>();
  const [visible, setVisible] = useState(false);
  const urlProductId = getParamFromUrl('productId');
  const isDemo = config.demoProduct?.some(item => item.productId === urlProductId);
  const onCascaderClick = async () => {
    if (!loadStatus) {
      setLoadStatus('loading');
      isDemo
        ? await loadDemoProducts() : await loadProducts();
      setLoadStatus('loaded');
    }
  };

  const displayRender = (app?: IProductInfo) => (app ? (
      <div className={style.appNameItem}>
        {getAppIcon(`${app?.platformId}`)}
        <span style={{ flex: 1 }}>{app?.productName ?? ''}</span>
        <AppSlot apps={[MultiApp.Pro]}>
          {productType === ProductType.FREE_PRODUCT && (
            <span className={style.free}>体验</span>
          )}
          {productType === ProductType.NORMAL_PRODUCT && (
            <span className={style.normal}>待付费</span>
          )}
          {productType === ProductType.PAID_PRODUCT
            && Number(app.platformId) !== Platform.HarmonyOS && (
              <span className={style.paid}>付费</span>
          )}
          {productType === ProductType.PAID_PRODUCT
            && Number(app.platformId) === Platform.HarmonyOS && (
            <span className={style.paid}>限免</span>
          )}
        </AppSlot>
      </div>
  ) : null);

  const labelRender = (app?: IProductInfo) => (app ? <div className={style.appNameItem}>
    {getAppIcon(`${app?.platformId}`)}
    <span>{app?.productName ?? ''}</span>
  </div> : null);

  const options: SelectedOptionsType[] = products.map((app: IProductInfo) => ({
    key: app.productId,
    value: app.productId,
    appName: app.productName,
    pid: app.platformId,
    label: labelRender(app),
    extra: app,
  }));

  const onChange: any = (_: string[], selectedOptions: SelectedOptionsType[]) => {
    const appId = selectedOptions[0].value;
    const { pid } = selectedOptions[0];
    if (!appId || !pid) {
      return;
    }
    changeProduct(appId, pid, '/overview');
  };

  const filter = (inputValue: string, path: any[]) => (
    path.some(option => option.appName?.toLowerCase()?.indexOf(inputValue.toLowerCase()) > -1)
  );

  const createApp = async () => {
    const isValues = await allowCreateProduct(history);
    if (!isValues) {
      return;
    }
    setVisible(true);
  };

  const groupOptions: any = useMemo(() => {
    const groups: any = {
      [ProductType.FREE_PRODUCT]: [],
      [ProductType.NORMAL_PRODUCT]: [],
      [ProductType.PAID_PRODUCT]: [],
      [LIMIT_FREE_PRODUCT]: [],
    };

    const filteredProducts = products.filter((product: IProductInfo) => {
      if (!keywords) return true;

      return (product.productName || '')?.toLowerCase()?.indexOf(keywords.toLowerCase()) > -1;
    });

    filteredProducts.forEach((product: IProductInfo) => {
      if (product.platformId === Platform.HarmonyOS) {
        groups[LIMIT_FREE_PRODUCT].push({
          key: product.productId,
          value: product.productId,
          appName: product.productName,
          pid: product.platformId,
          label: labelRender(product),
          extra: product,
        });
      } else if (has(groups, product.productType as IProductType) && product.platformId !== Platform.HarmonyOS) {
        groups[product.productType as IProductType].push({
          key: product.productId,
          value: product.productId,
          appName: product.productName,
          pid: product.platformId,
          label: labelRender(product),
          extra: product,
        });
      }
    });
    const res = [
      {
        label: '付费产品',
        options: groups[ProductType.PAID_PRODUCT],
      },
      {
        label: '体验产品',
        options: groups[ProductType.FREE_PRODUCT],
      },
      {
        label: '待付费产品',
        options: groups[ProductType.NORMAL_PRODUCT],
      },
      {
        label: '限免产品',
        options: groups[LIMIT_FREE_PRODUCT],
      },
    ].filter(item => !isEmpty(item.options));

    return res;
  }, [products, keywords]);

  const dropdownRender = (menus: ReactNode) => {
    if (loadStatus !== 'loaded') {
      return <div className={style.spinBox}>
        <Spin />
      </div>;
    }

    return (
      <div className={style.appList}>
        {isDemo && <div className={style.appListTitle}>{isDemo ? '体验Demo' : '我参与的产品'}</div>}
        <AppSlot apps={[MultiApp.Pro]}>
          {!isDemo && <div className={style.scrollList}>
            {groupOptions.map((group: any) => (
                  <div>
                    <div className={style.groupItem}>{group.label}</div>
                    {group.options.map((
                      opt: any,
                      index: number,
                    ) => <div className={style.subItem}
                    onClick={() => {
                      setPopVisible(false);
                      onChange([], [opt]);
                    }}
                    key={index}>{labelRender(opt.extra as unknown as IProductInfo)}</div>)}
                  </div>
            ))}
          </div>}
          {isDemo ? menus : null}
        </AppSlot>
        <AppSlot apps={[MultiApp.Sgp]}>
          {!isDemo && <div className={style.scrollList}>
            {options.map((option: any, index: number) => (
                <div className={style.subItem}
                  onClick={() => {
                    setPopVisible(false);
                    onChange([], [option]);
                  }}
                  key={index}>{labelRender(option.extra as unknown as IProductInfo)}</div>
            ))}
          </div>}
          {isDemo ? menus : null}
        </AppSlot>
        <div className={style.appListItem} onClick={() => history.push('/workbench/apps')}>
          <Space>
            <AppstoreOutlined />
            <span>{isDemo ? '我的产品' : '全部产品'}</span>
          </Space>
          <RightOutlined className={style.suffixIcon} />
        </div>
        {!isDemo ? <Fragment>
          <Divider style={{ margin: '4px 0' }} />
          <div
            className={style.appListItem}
            onClick={createApp}
          >
            <Space>
              <PlusSquareOutlined />
              <span>创建产品</span>
            </Space>
            <RightOutlined className={style.suffixIcon} />
          </div>
        </Fragment> : null}
      </div>
    );
  };

  return (
    <div ref={appRef} className={style.productSelector}>
      <Cascader
        options={isDemo ? options : undefined}
        value={[productId]}
        allowClear={false}
        showSearch={{ filter }}
        getPopupContainer={() => appRef?.current as HTMLDivElement}
        style={{ minWidth: '200px' }}
        onChange={onChange}
        onSearch={val => setKeywords(val)}
        displayRender={() => displayRender(product as unknown as IProductInfo)}
        dropdownRender={dropdownRender}
        placeholder={'请选择产品'}
        onClick={onCascaderClick}
        popupVisible={popupVisible}
        onPopupVisibleChange={v => setPopVisible(v)}
      />
      {product && (
        <div className={style.platformName}>
          <Tag color='#EDF1F7' style={{ color: '#0A6CFF' }}>{getPlatformName(Number(product.platformId))}</Tag>
        </div>
      )}
      <CreateApp visible={visible} handleCancel={() => setVisible(false)} />
    </div>
  );
};
